/**
 * Initiatives Slider — UGC-style card flip-through
 *
 * Replaces Swiper for the thrive-initiatives block. The active slide flings
 * to the right on "next"; the incoming slide rises up from below. Logic is
 * deliberately parallel to ugc-slider.js so the two are easy to maintain
 * together.
 */

const TRANSITION_MS = 500;
const DISMISSED     = 'block-thrive-initiatives__slide--dismissed';
const NO_TRANSITION = 'block-thrive-initiatives__slide--no-transition';

function slotClass( n ) {
	return `block-thrive-initiatives__slide--slot-${ n }`;
}

export const InitiativesSlider = {
	init() {
		document.querySelectorAll( '[data-swiper="thrive-initiatives"]' ).forEach( ( el ) => {
			if ( el.dataset.thriveInitiativesInit === '1' ) return;
			el.dataset.thriveInitiativesInit = '1';

			const container = el.closest( '[data-thrive-initiatives]' );
			if ( ! container ) return;

			const slides = Array.from( el.querySelectorAll( '.block-thrive-initiatives__slide' ) );
			const total  = slides.length;

			if ( total < 1 ) return;

			// Deepest slot this stack actually uses. Only slots 1..4 are styled,
			// so a stack of 2 bottoms out at slot-2.
			const BACK_SLOT = Math.min( total, 4 );

			const wrapper   = el.querySelector( '.swiper-wrapper' );
			const counterEl = container.querySelector( '[data-swiper-current]' );
			const prevBtn   = container.querySelector( '[data-swiper-prev]' );
			const nextBtn   = container.querySelector( '[data-swiper-next]' );

			let current   = 0;
			let animating = false;
			let heightRaf = 0;

			// Bumped on every goNext()/goPrev() call. Each call captures its own
			// value and every DEFERRED step (the rAF, the setTimeout, the rAF
			// inside that setTimeout) re-checks it before touching the DOM —
			// if a newer interaction started in the meantime, the stale
			// callback bails instead of applying. Without this, rapid repeat
			// clicks let an old callback's slot-class write land AFTER a newer
			// one's, leaving a slide with two conflicting slot classes (e.g.
			// slot-4 AND slot-1) whose equal-specificity CSS resolves to
			// opacity: 0 — a permanently blank slide.
			let epoch = 0;

			function syncStackHeight() {
				if ( ! wrapper ) return;

				if ( heightRaf ) {
					cancelAnimationFrame( heightRaf );
				}

				heightRaf = requestAnimationFrame( () => {
					heightRaf = 0;
					const maxHeight = slides.reduce( ( max, slide ) => {
						return Math.max( max, Math.ceil( slide.getBoundingClientRect().height ) );
					}, 0 );

					if ( maxHeight > 0 ) {
						wrapper.style.minHeight = `${ maxHeight }px`;
					}
				} );
			}

			function getSlot( idx ) {
				// Slot 1 = active (front), higher numbers = further back
				return ( ( idx - current + total ) % total ) + 1;
			}

			// Always clear the full 1..4 range, NOT 1..total. The stack only ever
			// *applies* slots 1..min(total,4), but a narrower clear loop leaves a
			// higher slot class stuck on the element forever — and since all slot
			// selectors have equal specificity, the later-declared
			// `--slot-3,--slot-4 { opacity: 0 }` then beats `--slot-1 { opacity: 1 }`
			// and the active slide renders blank. That was the blank-slide bug on
			// 2-slide sliders.
			function clearSlot( slide ) {
				for ( let i = 1; i <= 4; i++ ) {
					slide.classList.remove( slotClass( i ) );
				}
				slide.classList.remove( DISMISSED );
				slide.style.opacity = '';
			}

			function applySlots() {
				slides.forEach( ( s, i ) => {
					clearSlot( s );
					s.classList.add( slotClass( Math.min( getSlot( i ), 4 ) ) );
				} );
				syncStackHeight();
			}

			function updateUI() {
				if ( counterEl ) {
					counterEl.textContent = String( current + 1 ).padStart( 2, '0' );
				}
			}

			// Restart photo entry animations on the newly active slide
			function triggerPhotoEnter( slide ) {
				slides.forEach( ( s ) => s.removeAttribute( 'data-photo-enter' ) );
				void slide.offsetHeight; // force reflow so animation restarts
				slide.setAttribute( 'data-photo-enter', '' );
			}

			function goNext() {
				if ( animating ) return;
				animating = true;
				const myEpoch = ++epoch;

				// Current slide flings away to the right
				const dismissed    = slides[ current ];
				const prevCurrent  = current;
				clearSlot( dismissed );
				dismissed.classList.add( DISMISSED );

				current = ( current + 1 ) % total;

				// Remaining slides shift slots (incoming rises from slot-2 → slot-1)
				slides.forEach( ( s, i ) => {
					if ( i === prevCurrent ) return;
					clearSlot( s );
					s.classList.add( slotClass( Math.min( getSlot( i ), 4 ) ) );
				} );
				syncStackHeight();

				triggerPhotoEnter( slides[ current ] );
				updateUI();

				// After animation, teleport dismissed to back of stack invisibly
				setTimeout( () => {
					if ( myEpoch !== epoch ) return; // superseded — a newer click already moved things on
					dismissed.classList.add( NO_TRANSITION );
					clearSlot( dismissed );
					// Back of the stack is slot `total` (capped at 4) — NOT always 4.
					// A 2-slide stack only styles slots 1-2, so hardcoding 4 here
					// applied a class the layout never accounts for.
					dismissed.classList.add( slotClass( BACK_SLOT ) );
					dismissed.style.opacity = '0';

					void dismissed.offsetHeight; // force reflow

					dismissed.classList.remove( NO_TRANSITION );
					requestAnimationFrame( () => {
						if ( myEpoch === epoch ) {
							dismissed.style.opacity = '';
						}
						animating = false;
					} );
				}, TRANSITION_MS + 50 );
			}

			function goPrev() {
				if ( animating ) return;
				animating = true;
				const myEpoch = ++epoch;

				// Incoming slide is instantly placed at dismissed position, then
				// transitions into slot-1 (slides in from the right — same as UGC).
				const incomingIdx = ( current - 1 + total ) % total;
				const incoming    = slides[ incomingIdx ];

				incoming.classList.add( NO_TRANSITION );
				clearSlot( incoming );
				incoming.classList.add( DISMISSED );

				void incoming.offsetHeight; // force reflow

				incoming.classList.remove( NO_TRANSITION );

				requestAnimationFrame( () => {
					// Superseded — a newer click already moved things on and OWNS
					// `animating` now; touching it here would let a third click
					// sneak in while that newer call is still mid-flight.
					if ( myEpoch !== epoch ) return;
					current = incomingIdx;
					slides.forEach( ( s, i ) => {
						clearSlot( s );
						s.classList.add( slotClass( Math.min( getSlot( i ), 4 ) ) );
					} );
					syncStackHeight();
					triggerPhotoEnter( slides[ current ] );
					updateUI();
					setTimeout( () => {
						if ( myEpoch === epoch ) animating = false;
					}, TRANSITION_MS );
				} );
			}

			if ( nextBtn ) nextBtn.addEventListener( 'click', goNext );
			if ( prevBtn ) prevBtn.addEventListener( 'click', goPrev );

			if ( 'ResizeObserver' in window ) {
				const resizeObserver = new ResizeObserver( syncStackHeight );
				slides.forEach( ( slide ) => resizeObserver.observe( slide ) );
			}

			window.addEventListener( 'resize', syncStackHeight );

			if ( document.fonts && document.fonts.ready ) {
				document.fonts.ready.then( syncStackHeight );
			}

			// Swipe support
			let touchStartX = 0;
			let touchStartY = 0;
			const SWIPE_THRESHOLD = 50;

			el.addEventListener( 'touchstart', ( e ) => {
				touchStartX = e.changedTouches[ 0 ].screenX;
				touchStartY = e.changedTouches[ 0 ].screenY;
			}, { passive: true } );

			el.addEventListener( 'touchend', ( e ) => {
				const diffX = touchStartX - e.changedTouches[ 0 ].screenX;
				const diffY = Math.abs( touchStartY - e.changedTouches[ 0 ].screenY );
				if ( Math.abs( diffX ) > SWIPE_THRESHOLD && Math.abs( diffX ) > diffY ) {
					if ( diffX > 0 ) goNext();
					else goPrev();
				}
			}, { passive: true } );

			// Backgrounding the tab can freeze an in-flight opacity transition:
			// Chrome throttles the animation timeline while hidden, so the
			// Animation's playState stays "running" but currentTime never
			// advances past 0 — the incoming slide is left permanently
			// invisible (opacity: 0) once the tab is foregrounded again.
			// Cancel any stuck animations and reassert the correct slot
			// classes on resume so this can't leave a blank slide.
			document.addEventListener( 'visibilitychange', () => {
				if ( document.hidden ) return;
				slides.forEach( ( s ) => {
					if ( s.getAnimations ) {
						s.getAnimations().forEach( ( a ) => a.cancel() );
					}
				} );
				applySlots();
			} );

			// Init
			applySlots();
			syncStackHeight();
			triggerPhotoEnter( slides[ 0 ] );
			updateUI();
		} );
	},
};
